import React, { useState } from 'react';
import {
  Title,
  Text,
  Stack,
  Tabs,
  TextInput,
  Select,
  Switch,
  Button,
  Group,
  Card,
  SimpleGrid,
  Textarea,
  Avatar,
  FileButton,
  MultiSelect,
  PasswordInput,
  Divider,
  Alert,
  Badge,
} from '@mantine/core';
import {
  IconUser,
  IconBell,
  IconShield,
  IconMail,
  IconPhone,
  IconCamera,
  IconCheck,
  IconAlertCircle,
  IconDeviceMobile,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../contexts/AuthContext';

const ParentSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string | null>('profile');
  const [saving, setSaving] = useState(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);

  // Form states
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    secondary_email: '',
    emergency_phone: '',
    address: '',
    relationship_to_children: 'mother',
  });

  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    sms_notifications: true,
    push_notifications: true,
    notification_types: ['attendance', 'activities', 'photos', 'reports', 'alerts'],
    quiet_hours: false,
    quiet_start: '20:00',
    quiet_end: '08:00',
    frequency: 'realtime',
  });

  const [privacySettings, setPrivacySettings] = useState({
    share_photos: true,
    allow_teacher_messages: true,
    show_child_in_group_photos: true,
    data_retention: '1year',
    activity_tracking: true,
  });

  const [securitySettings, setSecuritySettings] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
    two_factor_enabled: false,
    login_alerts: true,
  });

  const handleSave = async (section: string) => {
    setSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    
    notifications.show({
      title: 'Settings Saved',
      message: `Your ${section} settings have been updated successfully`,
      color: 'green',
      icon: <IconCheck size={18} />,
    });
  };

  const handleImageUpload = (file: File | null) => {
    if (file) {
      setProfileImage(file);
      notifications.show({
        title: 'Image Uploaded',
        message: 'Your profile image has been updated',
        color: 'green',
      });
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2}>Settings</Title>
          <Text size="sm" c="dimmed">
            Manage your account settings and preferences
          </Text>
        </div>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>
            Profile
          </Tabs.Tab>
          <Tabs.Tab value="notifications" leftSection={<IconBell size={16} />}>
            Notifications
          </Tabs.Tab>
          <Tabs.Tab value="privacy" leftSection={<IconShield size={16} />}>
            Privacy
          </Tabs.Tab>
          <Tabs.Tab value="security" leftSection={<IconShield size={16} />}>
            Security
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="profile" pt="lg">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Profile Information</Title>
              
              <Group align="flex-start">
                <Avatar size={120} radius="md" src={profileImage ? URL.createObjectURL(profileImage) : null}>
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </Avatar>
                <Stack gap="xs">
                  <FileButton onChange={handleImageUpload} accept="image/png,image/jpeg">
                    {(props) => (
                      <Button {...props} variant="light" leftSection={<IconCamera size={16} />}>
                        Change Photo
                      </Button>
                    )}
                  </FileButton>
                  <Text size="xs" c="dimmed">
                    Recommended: Square image, at least 200x200px
                  </Text>
                </Stack>
              </Group>

              <Divider />

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                <TextInput
                  label="First Name"
                  placeholder="Enter your first name"
                  value={profileData.first_name}
                  onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                  required
                />
                <TextInput
                  label="Last Name"
                  placeholder="Enter your last name"
                  value={profileData.last_name}
                  onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                  required
                />
                <TextInput
                  label="Primary Email"
                  placeholder="your@email.com"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  leftSection={<IconMail size={16} />}
                  required
                />
                <TextInput
                  label="Phone Number"
                  placeholder="+1 234 567 8900"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  leftSection={<IconPhone size={16} />}
                  required
                />
                <TextInput
                  label="Secondary Email"
                  placeholder="secondary@email.com"
                  value={profileData.secondary_email}
                  onChange={(e) => setProfileData({ ...profileData, secondary_email: e.target.value })}
                  leftSection={<IconMail size={16} />}
                />
                <TextInput
                  label="Emergency Contact"
                  placeholder="+1 234 567 8900"
                  value={profileData.emergency_phone}
                  onChange={(e) => setProfileData({ ...profileData, emergency_phone: e.target.value })}
                  leftSection={<IconPhone size={16} />}
                />
                <Select
                  label="Relationship to Children"
                  placeholder="Select relationship"
                  data={[
                    { value: 'mother', label: 'Mother' },
                    { value: 'father', label: 'Father' },
                    { value: 'guardian', label: 'Guardian' },
                    { value: 'grandparent', label: 'Grandparent' },
                    { value: 'other', label: 'Other' },
                  ]}
                  value={profileData.relationship_to_children}
                  onChange={(value) => setProfileData({ ...profileData, relationship_to_children: value || 'mother' })}
                />
              </SimpleGrid>

              <Textarea
                label="Address"
                placeholder="Enter your address"
                value={profileData.address}
                onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                rows={3}
              />

              <Group justify="flex-end" mt="md">
                <Button loading={saving} onClick={() => handleSave('profile')}>
                  Save Profile
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="notifications" pt="lg">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Notification Preferences</Title>
              
              <Alert color="blue" icon={<IconAlertCircle size={16} />}>
                Configure how and when you receive notifications about your children's activities
              </Alert>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                <Switch
                  label="Email Notifications"
                  description="Receive updates via email"
                  checked={notificationSettings.email_notifications}
                  onChange={(e) => setNotificationSettings({
                    ...notificationSettings,
                    email_notifications: e.currentTarget.checked
                  })}
                />
                <Switch
                  label="SMS Notifications"
                  description="Receive text messages for urgent updates"
                  checked={notificationSettings.sms_notifications}
                  onChange={(e) => setNotificationSettings({
                    ...notificationSettings,
                    sms_notifications: e.currentTarget.checked
                  })}
                />
                <Switch
                  label="Push Notifications"
                  description="Receive mobile app notifications"
                  checked={notificationSettings.push_notifications}
                  onChange={(e) => setNotificationSettings({
                    ...notificationSettings,
                    push_notifications: e.currentTarget.checked
                  })}
                />
                <Switch
                  label="Quiet Hours"
                  description="Pause non-urgent notifications"
                  checked={notificationSettings.quiet_hours}
                  onChange={(e) => setNotificationSettings({
                    ...notificationSettings,
                    quiet_hours: e.currentTarget.checked
                  })}
                />
              </SimpleGrid>

              {notificationSettings.quiet_hours && (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <TextInput
                    label="Quiet Hours Start"
                    type="time"
                    value={notificationSettings.quiet_start}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      quiet_start: e.target.value
                    })}
                  />
                  <TextInput
                    label="Quiet Hours End"
                    type="time"
                    value={notificationSettings.quiet_end}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      quiet_end: e.target.value
                    })}
                  />
                </SimpleGrid>
              )}

              <MultiSelect
                label="Notification Types"
                description="Choose which types of notifications to receive"
                data={[
                  { value: 'attendance', label: 'Attendance Updates' },
                  { value: 'activities', label: 'Activity Reports' },
                  { value: 'photos', label: 'New Photos' },
                  { value: 'reports', label: 'Daily Reports' },
                  { value: 'alerts', label: 'Safety Alerts' },
                  { value: 'messages', label: 'Teacher Messages' },
                  { value: 'events', label: 'School Events' },
                  { value: 'milestones', label: 'Child Milestones' },
                ]}
                value={notificationSettings.notification_types}
                onChange={(value) => setNotificationSettings({
                  ...notificationSettings,
                  notification_types: value
                })}
              />

              <Select
                label="Notification Frequency"
                description="How often to receive batch notifications"
                data={[
                  { value: 'realtime', label: 'Real-time (as they happen)' },
                  { value: 'hourly', label: 'Hourly Summary' },
                  { value: 'daily', label: 'Daily Digest' },
                  { value: 'weekly', label: 'Weekly Summary' },
                ]}
                value={notificationSettings.frequency}
                onChange={(value) => setNotificationSettings({
                  ...notificationSettings,
                  frequency: value || 'realtime'
                })}
              />

              <Group justify="flex-end" mt="md">
                <Button loading={saving} onClick={() => handleSave('notification')}>
                  Save Notification Settings
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="privacy" pt="lg">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Privacy Settings</Title>
              
              <Stack gap="md">
                <Switch
                  label="Share Photos with Other Parents"
                  description="Allow your child's photos to be visible to other parents in the class"
                  checked={privacySettings.share_photos}
                  onChange={(e) => setPrivacySettings({
                    ...privacySettings,
                    share_photos: e.currentTarget.checked
                  })}
                />
                
                <Switch
                  label="Allow Teacher Direct Messages"
                  description="Teachers can send you direct messages about your child"
                  checked={privacySettings.allow_teacher_messages}
                  onChange={(e) => setPrivacySettings({
                    ...privacySettings,
                    allow_teacher_messages: e.currentTarget.checked
                  })}
                />
                
                <Switch
                  label="Include Child in Group Photos"
                  description="Your child can appear in group activity photos"
                  checked={privacySettings.show_child_in_group_photos}
                  onChange={(e) => setPrivacySettings({
                    ...privacySettings,
                    show_child_in_group_photos: e.currentTarget.checked
                  })}
                />
                
                <Switch
                  label="Activity Tracking"
                  description="Track and analyze your child's daily activities"
                  checked={privacySettings.activity_tracking}
                  onChange={(e) => setPrivacySettings({
                    ...privacySettings,
                    activity_tracking: e.currentTarget.checked
                  })}
                />
              </Stack>

              <Divider />

              <Select
                label="Data Retention Period"
                description="How long to keep your child's activity data"
                data={[
                  { value: '3months', label: '3 Months' },
                  { value: '6months', label: '6 Months' },
                  { value: '1year', label: '1 Year' },
                  { value: '2years', label: '2 Years' },
                  { value: 'forever', label: 'Keep Forever' },
                ]}
                value={privacySettings.data_retention}
                onChange={(value) => setPrivacySettings({
                  ...privacySettings,
                  data_retention: value || '1year'
                })}
              />

              <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                Privacy settings affect how your child's information is shared within the school community.
                Changes take effect immediately.
              </Alert>

              <Group justify="flex-end" mt="md">
                <Button loading={saving} onClick={() => handleSave('privacy')}>
                  Save Privacy Settings
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="security" pt="lg">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Security Settings</Title>
              
              <Alert color="blue" icon={<IconShield size={16} />}>
                Keep your account secure by using a strong password and enabling two-factor authentication
              </Alert>

              <Divider label="Change Password" />

              <SimpleGrid cols={{ base: 1, md: 1 }} spacing="md">
                <PasswordInput
                  label="Current Password"
                  placeholder="Enter current password"
                  value={securitySettings.current_password}
                  onChange={(e) => setSecuritySettings({
                    ...securitySettings,
                    current_password: e.target.value
                  })}
                />
                <PasswordInput
                  label="New Password"
                  placeholder="Enter new password"
                  value={securitySettings.new_password}
                  onChange={(e) => setSecuritySettings({
                    ...securitySettings,
                    new_password: e.target.value
                  })}
                />
                <PasswordInput
                  label="Confirm New Password"
                  placeholder="Confirm new password"
                  value={securitySettings.confirm_password}
                  onChange={(e) => setSecuritySettings({
                    ...securitySettings,
                    confirm_password: e.target.value
                  })}
                />
              </SimpleGrid>

              <Divider label="Additional Security" />

              <Stack gap="md">
                <Group justify="space-between">
                  <div>
                    <Text fw={500}>Two-Factor Authentication</Text>
                    <Text size="sm" c="dimmed">
                      Add an extra layer of security to your account
                    </Text>
                  </div>
                  <Switch
                    checked={securitySettings.two_factor_enabled}
                    onChange={(e) => setSecuritySettings({
                      ...securitySettings,
                      two_factor_enabled: e.currentTarget.checked
                    })}
                  />
                </Group>

                <Group justify="space-between">
                  <div>
                    <Text fw={500}>Login Alerts</Text>
                    <Text size="sm" c="dimmed">
                      Get notified when someone logs into your account
                    </Text>
                  </div>
                  <Switch
                    checked={securitySettings.login_alerts}
                    onChange={(e) => setSecuritySettings({
                      ...securitySettings,
                      login_alerts: e.currentTarget.checked
                    })}
                  />
                </Group>
              </Stack>

              <Divider label="Active Sessions" />

              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconDeviceMobile size={20} />
                    <div>
                      <Text size="sm" fw={500}>iPhone 13 Pro</Text>
                      <Text size="xs" c="dimmed">Tokyo, Japan • Current session</Text>
                    </div>
                  </Group>
                  <Badge color="green">Active</Badge>
                </Group>
                <Group justify="space-between">
                  <Group>
                    <IconDeviceMobile size={20} />
                    <div>
                      <Text size="sm" fw={500}>Chrome on Windows</Text>
                      <Text size="xs" c="dimmed">Osaka, Japan • 2 days ago</Text>
                    </div>
                  </Group>
                  <Button size="xs" variant="light" color="red">
                    Sign Out
                  </Button>
                </Group>
              </Stack>

              <Group justify="flex-end" mt="md">
                <Button loading={saving} onClick={() => handleSave('security')}>
                  Update Security Settings
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

export default ParentSettingsPage;